/** Pure helpers for the dashboard (unit-tested). */

export type RangeDays = 7 | 30 | 90;

export const RANGE_OPTIONS: { days: RangeDays; label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export interface DateRange {
  from: string;
  to: string;
}

const DAY = 86_400_000;

/** `YYYY-MM-DD` for an instant, in the given IANA time zone (the shop's business date). */
export function isoDateIn(instant: Date, timeZone: string): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    return instant.toISOString().slice(0, 10);
  }
}

/** Adds whole days to a `YYYY-MM-DD` date (calendar arithmetic, no time-zone drift). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + days * DAY;
  return new Date(t).toISOString().slice(0, 10);
}

/** The last `days` business days ending today (inclusive), and the equally long period before. */
export function dashboardRanges(
  today: string,
  days: RangeDays,
): { current: DateRange; previous: DateRange } {
  const from = addDays(today, -(days - 1));
  return {
    current: { from, to: today },
    previous: { from: addDays(from, -days), to: addDays(from, -1) },
  };
}

/** Chart bucket size for a range: daily up to a month, weekly beyond. */
export function intervalFor(days: RangeDays): 'day' | 'week' {
  return days > 31 ? 'week' : 'day';
}

/**
 * Relative change from `previous` to `current` as a fraction (0.25 = +25 %). `null` when there is
 * no meaningful base to compare against (previous is zero or missing).
 */
export function percentChange(
  current: string | number | null | undefined,
  previous: string | number | null | undefined,
): number | null {
  const c = Number(current ?? 0);
  const p = Number(previous ?? 0);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
  return (c - p) / Math.abs(p);
}

/** `+12%`, `−8%`, `0%` (true minus sign; rounds to whole percent, one decimal below 10 %). */
export function formatChange(change: number): string {
  const pct = Math.abs(change * 100);
  const digits = pct < 10 && pct !== 0 && Math.round(pct) !== pct ? 1 : 0;
  const text = `${pct.toFixed(digits).replace(/\.0$/, '')}%`;
  if (Math.abs(change) < 0.0005) return '0%';
  return `${change > 0 ? '+' : '−'}${text}`;
}

export interface AgingTotals {
  current: string;
  days1To30: string;
  days31To60: string;
  days61To90: string;
  over90: string;
  outstanding: string;
}

export interface AgingBucket {
  key: 'current' | 'days1To30' | 'days31To60' | 'days61To90' | 'over90';
  label: string;
  amount: number;
  /** Share of the outstanding total, 0–1. */
  share: number;
  tone: 'success' | 'warning' | 'orange' | 'error' | 'critical';
}

const BUCKETS: Pick<AgingBucket, 'key' | 'label' | 'tone'>[] = [
  { key: 'current', label: 'Not yet due', tone: 'success' },
  { key: 'days1To30', label: '1–30 days', tone: 'warning' },
  { key: 'days31To60', label: '31–60 days', tone: 'orange' },
  { key: 'days61To90', label: '61–90 days', tone: 'error' },
  { key: 'over90', label: '90+ days', tone: 'critical' },
];

/** Aging buckets with their share of the total (shares sum to 1 when anything is owed). */
export function agingBuckets(totals: AgingTotals): AgingBucket[] {
  const amounts = BUCKETS.map((b) => Math.max(0, Number(totals[b.key]) || 0));
  const sum = amounts.reduce((s, v) => s + v, 0);
  return BUCKETS.map((b, i) => {
    const amount = amounts[i] ?? 0;
    return { ...b, amount, share: sum ? amount / sum : 0 };
  });
}

/** Everything past its due date (all buckets except "current"). */
export function overdueAmount(totals: AgingTotals): number {
  return agingBuckets(totals)
    .filter((b) => b.key !== 'current')
    .reduce((s, b) => s + b.amount, 0);
}

/** Low-stock severity: out of stock, below half the reorder level, or just low. */
export function stockSeverity(available: string, reorderLevel: string): 'out' | 'critical' | 'low' {
  const a = Number(available);
  const r = Number(reorderLevel);
  if (a <= 0) return 'out';
  if (r > 0 && a <= r / 2) return 'critical';
  return 'low';
}

/** Fill level for a low-stock bar: available as a share of the reorder level, 0–1. */
export function stockFill(available: string, reorderLevel: string): number {
  const a = Number(available);
  const r = Number(reorderLevel);
  if (!(r > 0)) return a > 0 ? 1 : 0;
  return Math.min(1, Math.max(0, a / r));
}

/** Time-of-day greeting in the shop's time zone. */
export function greeting(now: Date, timeZone: string): string {
  let hour = now.getHours();
  try {
    hour = Number(
      new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(now),
    );
  } catch {
    // keep the local hour
  }
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}

/** `21 Sep` for chart axes (business dates, no time-zone shift). */
export function axisDate(date: string, locale: string): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return date;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, d)));
  } catch {
    return date.slice(5, 10);
  }
}

export interface MixSlice {
  id: string;
  label: string;
  value: number;
  color: string;
}

const SLICE_COLORS = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
];

/**
 * Money received per payment method (customer receipts only), largest first. Up to five methods
 * keep their own colour; the rest fold into "Other" so no extra hue is ever generated.
 */
export function paymentMix(
  rows: { methodId: string; method: string; purpose: string; direction: string; amount: string }[],
): MixSlice[] {
  const by = new Map<string, { label: string; value: number }>();
  for (const r of rows) {
    if (r.purpose !== 'RECEIPT' || r.direction !== 'IN') continue;
    const row = by.get(r.methodId) ?? { label: r.method, value: 0 };
    row.value += Number(r.amount);
    by.set(r.methodId, row);
  }
  const sorted = [...by.entries()]
    .map(([id, v]) => ({ id, ...v }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  const keep = sorted.length > 5 ? 4 : 5;
  const slices: MixSlice[] = sorted
    .slice(0, keep)
    .map((s, i) => ({ ...s, color: SLICE_COLORS[i] ?? 'var(--viz-5)' }));
  const rest = sorted.slice(keep);
  if (rest.length)
    slices.push({
      id: 'other',
      label: `Other (${String(rest.length)})`,
      value: rest.reduce((n, s) => n + s.value, 0),
      color: 'var(--mui-palette-text-disabled)',
    });
  return slices;
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export const HEATMAP_WEEKS = 12;

export interface HeatCell {
  date: string;
  total: number;
  invoices: number;
  /** 0 = no sales; 1–4 = quartile of the busiest day in the window. */
  level: 0 | 1 | 2 | 3 | 4;
  future: boolean;
}

/** Monday on or before a business date. */
export function weekStartOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  const dow = (new Date(t).getUTCDay() + 6) % 7;
  return new Date(t - dow * 86_400_000).toISOString().slice(0, 10);
}

/**
 * A weeks × weekdays grid of daily sales ending with the week of `today` (columns oldest first,
 * rows Monday to Sunday), with a 4-step level relative to the best day, and the average per weekday.
 */
export function salesHeatmap(
  rows: { periodStart: string; total: string; invoiceCount: number }[],
  today: string,
): { weeks: HeatCell[][]; weekdayAverage: number[]; first: string } {
  const byDate = new Map(rows.map((r) => [r.periodStart.slice(0, 10), r]));
  const first = addDays(weekStartOf(today), -(HEATMAP_WEEKS - 1) * 7);
  const max = Math.max(0, ...rows.map((r) => Number(r.total)));
  const sums = Array<number>(7).fill(0);
  const days = Array<number>(7).fill(0);
  const weeks = Array.from({ length: HEATMAP_WEEKS }, (_, w) =>
    WEEKDAYS.map((_, d): HeatCell => {
      const date = addDays(first, w * 7 + d);
      const row = byDate.get(date);
      const total = row ? Number(row.total) : 0;
      const future = date > today;
      if (!future) {
        sums[d] = (sums[d] ?? 0) + total;
        days[d] = (days[d] ?? 0) + 1;
      }
      const level =
        total <= 0 || max <= 0 ? 0 : (Math.min(4, Math.ceil((total / max) * 4)) as 1 | 2 | 3 | 4);
      return { date, total, invoices: row?.invoiceCount ?? 0, level, future };
    }),
  );
  return {
    weeks,
    weekdayAverage: sums.map((s, i) => (days[i] ? s / (days[i] ?? 1) : 0)),
    first,
  };
}
